import { useEffect, useMemo, useState } from "react";
import { Folder, FolderInput, Search } from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import type { MailFolder } from "@/lib/types/mail";

type Props = {
  open: boolean;
  folders: MailFolder[];
  currentFolder: string;
  title?: string;
  onSelect: (folderName: string) => void;
  onClose: () => void;
};

export function MailMoveFolderDialog({
  open,
  folders,
  currentFolder,
  title = "Déplacer vers",
  onSelect,
  onClose,
}: Props) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const options = useMemo(() => {
    const available = folders.filter((f) => f.name !== currentFolder);
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return available;
    return available.filter(
      (folder) =>
        folder.label.toLowerCase().includes(trimmed) ||
        folder.name.toLowerCase().includes(trimmed)
    );
  }, [currentFolder, folders, query]);

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy="mail-move-title"
      variant="center"
      panelClassName="mail-move-dialog max-w-lg"
    >
      <div className="flex max-h-[min(78vh,32rem)] flex-col">
        <div className="mail-move-dialog-header">
          <div className="mail-move-dialog-icon" aria-hidden>
            <FolderInput className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="mail-move-title" className="truncate text-sm font-semibold text-[var(--mail-text)]">
              {title}
            </h2>
            <p className="mt-0.5 text-[11px] text-[var(--mail-muted)]">
              Choisissez le dossier de destination
            </p>
          </div>
        </div>

        {folders.length > 6 ? (
          <div className="mail-move-dialog-search">
            <Search className="h-4 w-4 shrink-0 text-[var(--mail-muted)]" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtrer les dossiers…"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-[var(--mail-text)] placeholder:text-[var(--mail-muted)] focus:outline-none"
              autoFocus
            />
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {options.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[var(--mail-muted)]">
              {query.trim()
                ? "Aucun dossier ne correspond à votre recherche."
                : "Aucun autre dossier disponible."}
            </p>
          ) : (
            <ul className="space-y-0.5">
              {options.map((folder) => (
                <li key={folder.name}>
                  <button
                    type="button"
                    onClick={() => onSelect(folder.name)}
                    className="mail-move-dialog-item"
                    style={{ paddingLeft: `${14 + (folder.depth ?? 0) * 16}px` }}
                  >
                    <Folder className="h-4 w-4 shrink-0 text-[var(--mail-accent)]" aria-hidden />
                    <span className="min-w-0 truncate">{folder.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mail-move-dialog-footer">
          <button type="button" onClick={onClose} className="mail-move-dialog-cancel">
            Annuler
          </button>
        </div>
      </div>
    </MailModalOverlay>
  );
}
